<script setup lang="ts">
import type {SideBarSectionItem} from '@/ts/types/component/balm-sidebar';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import LightboxVideo from '@/vue/molecules/lightbox-video.vue';
import {ref} from 'vue';

defineProps<{
  items: SideBarSectionItem[]
  name: string;
}>();

const emit = defineEmits<{
  /** The entry's video was watched past the mark that counts. Carries the entry's id. */
  videoWatched: [UuidDTO];
}>();

/**
 * Which entries are open, driving the expansion items rather than following them.
 *
 * The expansion body is rendered while collapsed and merely hidden, so an eager video entry would
 * load its poster and its metadata for anyone who opens the panel, and would go on playing out of
 * sight once the entry is collapsed again, with the controls that could stop it hidden along with
 * it. Owning the state is what lets the player be mounted with the entry and taken away with it,
 * the moment it closes rather than when its animation ends.
 */
const opened = ref<Partial<Record<UuidDTO, boolean>>>({});
</script>
<template>
  <div class="section">
    <div v-if="name" class="section-category">
      {{ name }}
    </div>
    <q-expansion-item
      v-for="item in items"
      :key="item.id"
      class="section-item"
      :label="item.label"
      header-class="section-question"
      :model-value="opened[item.id] === true"
      @update:model-value="opened[item.id] = $event"
    >
      <div class="section-answer">
        <!-- The panel is narrow and sits over the page, so the video plays in place: no lightbox. -->
        <lightbox-video
          v-if="item.video && opened[item.id]"
          class="section-video"
          :video-src="item.video.src"
          :poster-src="item.video.posterSrc"
          :poster-alt="item.video.posterAlt"
          @half-watched="emit('videoWatched', item.id)"
        />
        <div v-else-if="item.html" v-safe-html="item.html"/>
      </div>
    </q-expansion-item>
  </div>
</template>
<style scoped lang="scss">
.section {
  margin: var(--Spacing-spacing-6) 0;
}


.section-category {
  padding-bottom: var(--Spacing-spacing-3);
  @include font-body-bold;
}

// eslint-disable-next-line vue-scoped-css/no-unused-selector
.section-item {
  margin-bottom: var(--Spacing-spacing-2);
  border-radius: var(--card-border-radius);
  background: var(--Neutral-UI-neutral-ui-20);

  :deep(.q-item) {
    padding: var(--Spacing-spacing-3);
    min-height: 0;
    border-radius: var(--card-border-radius);

    @include screen-md {
      padding: var(--Spacing-spacing-4) var(--Spacing-spacing-5);
    }

    &:focus-visible {
      outline: none;
      box-shadow: inset 0 0 0 2px var(--Primary-primary-color-1);
    }
  }

  :deep(.section-question) {
    @include font-body-sm-bold;
  }

  &.q-expansion-item--expanded {
    background-color: var(--Secondary-secondary-color-1);
    transition: background-color .3s;
  }
}

.section-answer {
  padding: var(--Spacing-spacing-3);
  @include font-body-sm;

  @include screen-md {
    padding: var(--Spacing-spacing-4) var(--Spacing-spacing-5);
  }
}

// The expansion body has no height of its own, so the player is given the ratio it plays at.
.section-video {
  aspect-ratio: 16 / 9;
  width: 100%;
}
</style>
